<template>
  <div class="bb-scoreboard">
    <BbScoreBar />

    <div class="content">
      <div v-if="isFinished" class="winner-card">
        <span class="winner-label">{{ winners.length > 1 ? 'Gedeelde winst' : 'Eindstand' }}</span>
        <span class="winner-name">{{ winners.map(w => w.name).join(' & ') }}</span>
        <span class="winner-score">{{ winners[0].score }} punten</span>
      </div>
      <div class="board-head">
        <p v-if="!isFinished" class="next-round">
          Ronde {{ roundNumber }} van {{ totalRounds }} ·
          {{ cardsNextRound }} {{ cardsNextRound === 1 ? 'kaart' : 'kaarten' }} ·
          deler {{ dealerName }}
        </p>
        <GameTransfer variant="inline" :show-import="false" />
      </div>

      <div v-if="scoreTable.rows.length === 0" class="empty">Nog geen rondes gespeeld</div>

      <div v-else ref="tableCard" class="table-card">
        <table class="score-table">
          <thead>
            <tr>
              <th class="round-col" scope="col">Ronde</th>
              <th v-for="player in scoreTable.players" :key="player.id" scope="col">{{ player.name }}</th>
              <th class="edit-col" scope="col"><span class="sr-only">Wijzigen</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in scoreTable.rows" :key="row.roundNumber">
              <th class="round-col" scope="row">
                <span class="round-number">{{ row.roundNumber }}</span>
                <span class="round-cards">{{ row.cards }}k</span>
                <span v-if="trumpFor(row)" class="round-trump" :class="{ red: trumpFor(row).red }">{{ trumpFor(row).symbol }}</span>
              </th>
              <td v-for="entry in row.entries" :key="entry.id">
                <span class="bid-taken" :class="{ made: entry.bid === entry.tricks }">
                  {{ entry.bid }} <span class="arrow">›</span> {{ entry.tricks }}
                </span>
                <span class="points-badge" :class="{ positive: entry.points > 0, negative: entry.points < 0 }">
                  {{ entry.points > 0 ? '+' : '' }}{{ entry.points }}
                </span>
              </td>
              <td class="edit-col">
                <button
                  v-if="row.roundNumber === lastRoundNumber"
                  class="edit-btn"
                  aria-label="Laatste ronde wijzigen"
                  @click="startEditLastRound"
                >✎</button>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <th class="round-col" scope="row">Totaal</th>
              <td v-for="player in scoreTable.players" :key="player.id" class="total-cell">
                {{ playerTotals[player.id] }}
              </td>
              <td class="edit-col"></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <AppFooter
      :continue-label="isFinished ? 'Nieuw spel' : `Ronde ${roundNumber} starten`"
      show-end-game
      @continue="isFinished ? startNewGame() : startRound()"
      @end-game="requestEndGame"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { useBoerenbridgeStore } from '../../stores/boerenbridge'
import { trumpById } from '../../models/boerenbridge'
import BbScoreBar from './BbScoreBar.vue'
import AppFooter from '../shared/AppFooter.vue'
import GameTransfer from '../shared/GameTransfer.vue'

export default {
  name: 'BbScoreboard',
  components: { BbScoreBar, AppFooter, GameTransfer },
  computed: {
    ...mapState(useBoerenbridgeStore, [
      'players',
      'roundNumber',
      'totalRounds',
      'roundPlan',
      'scoreTable',
      'playerTotals',
      'isFinished',
      'ranking',
      'activeDealerId'
    ]),
    /** Bij een gelijkstand delen meerdere spelers de eerste plaats. */
    winners() {
      const top = this.ranking[0]
      return top ? this.ranking.filter(p => p.score === top.score) : []
    },
    cardsNextRound() {
      return this.roundPlan[this.roundNumber - 1] ?? 0
    },
    dealerName() {
      return this.players.find(p => p.id === this.activeDealerId)?.name ?? ''
    },
    lastRoundNumber() {
      const rows = this.scoreTable.rows
      return rows.length ? rows[rows.length - 1].roundNumber : null
    }
  },
  mounted() {
    this.scrollToBottom()
  },
  updated() {
    this.scrollToBottom()
  },
  methods: {
    ...mapActions(useBoerenbridgeStore, ['startRound', 'startNewGame', 'requestEndGame', 'startEditLastRound']),
    trumpFor(row) {
      return trumpById(row.trump)
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.tableCard
        if (el) el.scrollTop = el.scrollHeight
      })
    }
  }
}
</script>

<style scoped>
.bb-scoreboard {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.content {
  flex: 1;
  min-height: 0;
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.board-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.next-round {
  flex: 1;
  min-width: 0;
  font-size: 0.82rem;
  color: #5a6d5a;
  text-align: center;
}

.board-head > .transfer {
  margin-left: auto;
}

.winner-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.15rem;
  padding: 0.75rem;
  background: #2c3e50;
  color: #fff;
  border-radius: 12px;
}

.winner-label {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  opacity: 0.7;
}

.winner-name {
  font-size: 1.35rem;
  font-weight: 700;
}

.winner-score {
  font-size: 0.9rem;
  opacity: 0.85;
}

.empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  color: #888;
}

.table-card {
  flex: 1;
  min-height: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.score-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.score-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #2c3e50;
  color: #fff;
  padding: 0.7rem 0.5rem;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  text-align: center;
  white-space: nowrap;
  min-width: 4.5rem;
}

.score-table thead .round-col {
  z-index: 3;
  left: 0;
}

.round-col {
  position: sticky;
  left: 0;
  min-width: 3.75rem;
  width: 3.75rem;
  background: #fff;
  text-align: left;
  padding: 0.5rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: #8a8a8a;
  white-space: nowrap;
}

.round-number {
  font-weight: 700;
  color: #2c3e50;
}

.round-cards {
  margin-left: 0.2rem;
  color: #aaa;
}

.round-trump { margin-left: 0.2rem; color: #2c3e50; }
.round-trump.red { color: #c0392b; }

.score-table tbody tr:nth-child(even) td,
.score-table tbody tr:nth-child(even) .round-col {
  background: #f6faf6;
}

.score-table tbody td,
.score-table tbody .round-col {
  border-bottom: 1px solid #eef3ee;
}

.score-table td {
  padding: 0.45rem 0.4rem;
  text-align: center;
  background: #fff;
}

.bid-taken {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #999;
  white-space: nowrap;
}

.bid-taken.made { color: #27ae60; }

.arrow { opacity: 0.5; }

.points-badge {
  display: inline-block;
  min-width: 2.5rem;
  margin-top: 0.15rem;
  padding: 0.2em 0.45em;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 700;
  background: #f0f0f0;
  color: #999;
}

.points-badge.positive { background: #e6f6ed; color: #27ae60; }
.points-badge.negative { background: #fdecea; color: #c0392b; }

.score-table tfoot th,
.score-table tfoot td {
  position: sticky;
  bottom: 0;
  z-index: 2;
  background: #eef5ee;
  border-top: 2px solid #aac8aa;
  padding: 0.55rem 0.4rem;
  text-align: center;
  font-size: 1rem;
  font-weight: 700;
  color: #2c3e50;
}

.score-table tfoot .round-col {
  z-index: 3;
  left: 0;
  text-align: left;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #2c3e50;
}

.edit-col {
  width: 3.25rem;
  min-width: 3.25rem;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}

.edit-btn {
  width: 2.25rem;
  height: 2.25rem;
  padding: 0;
  font-size: 1.05rem;
  font-family: inherit;
  border: 2px solid #2c3e50;
  border-radius: 8px;
  background: #fff;
  color: #2c3e50;
  cursor: pointer;
  touch-action: manipulation;
}

.edit-btn:active { opacity: 0.75; }

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Op tablet vult de tabel het scherm, met grotere cijfers en tikvlakken */
@media (min-width: 600px) {
  .content {
    padding: 1rem 1.5rem;
    gap: 0.75rem;
  }

  .next-round { font-size: 1.05rem; }

  .winner-card {
    padding: 1rem;
    border-radius: 16px;
  }

  .winner-label { font-size: 0.8rem; }

  .winner-name { font-size: 2rem; }

  .winner-score { font-size: 1.1rem; }

  .table-card { border-radius: 16px; }

  .score-table thead th {
    padding: 0.9rem 0.75rem;
    font-size: 0.9rem;
    min-width: 6rem;
  }

  .round-col {
    min-width: 5rem;
    width: 5rem;
    padding: 0.6rem 0.75rem;
    font-size: 0.9rem;
  }

  .score-table td { padding: 0.6rem 0.5rem; }

  .bid-taken { font-size: 0.95rem; }

  .points-badge {
    min-width: 3.25rem;
    font-size: 1.1rem;
    border-radius: 8px;
  }

  .score-table tfoot th,
  .score-table tfoot td {
    padding: 0.75rem 0.5rem;
    font-size: 1.35rem;
  }

  .score-table tfoot .round-col { font-size: 0.9rem; }

  .edit-col {
    width: 4rem;
    min-width: 4rem;
  }

  .edit-btn {
    width: 2.75rem;
    height: 2.75rem;
    font-size: 1.25rem;
  }
}
</style>
